import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RiCloseLine, RiShieldCheckLine, RiCheckLine } from '@remixicon/react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

const MfaEnrollModal = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [factorId, setFactorId] = useState(null);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const initialized = useRef(false);

  useEffect(() => {
    if (!open) {
      initialized.current = false;
      setFactorId(null);
      setQrCode('');
      setSecret('');
      setCode('');
      setError(null);
      return;
    }
    if (initialized.current) return;
    initialized.current = true;

    (async () => {
      setLoading(true);
      try {
        const { data: existing } = await supabase.auth.mfa.listFactors();
        const stale = (existing?.totp || []).filter((f) => f.status !== 'verified');
        for (const f of stale) {
          await supabase.auth.mfa.unenroll({ factorId: f.id });
        }

        const { data, error: enrollError } = await supabase.auth.mfa.enroll({
          factorType: 'totp',
        });
        if (enrollError) throw enrollError;
        setFactorId(data.id);
        setQrCode(data.totp.qr_code);
        setSecret(data.totp.secret);
      } catch (err) {
        setError(err.message || 'Failed to start MFA enrollment');
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const handleVerify = async () => {
    if (verifying || !factorId || code.length !== 6) return;
    setVerifying(true);
    setError(null);
    try {
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
      if (challengeError) throw challengeError;
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (verifyError) throw verifyError;
      toast.success('Two-factor authentication enabled', { position: 'bottom-right' });
      onSuccess?.();
    } catch (err) {
      setError(err.message || 'Invalid code, please try again');
    } finally {
      setVerifying(false);
    }
  };

  const handleCancel = async () => {
    if (factorId) {
      try {
        await supabase.auth.mfa.unenroll({ factorId });
      } catch {
        // ignore cleanup error
      }
    }
    onClose?.();
  };

  if (!open) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/30" onClick={handleCancel} />
      <div className="fixed z-[9999] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-md bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h3 className="text-lg font-semibold text-primary-700">Enable two-factor authentication</h3>
          <button
            type="button"
            onClick={handleCancel}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded-full"
            aria-label="Close"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                Scan this QR code with an authenticator app (Google Authenticator, Authy, 1Password), then enter the 6-digit code below.
              </p>

              {qrCode && (
                <div className="flex justify-center">
                  <img src={qrCode} alt="MFA QR code" className="w-48 h-48 bg-white rounded-lg border border-gray-200 p-2" />
                </div>
              )}

              {secret && (
                <div>
                  <p className="text-xs font-medium tracking-wider text-gray-500 uppercase mb-1">
                    Or enter this secret manually
                  </p>
                  <code className="block w-full text-center text-sm font-mono text-gray-800 bg-gray-50 border border-gray-200 rounded-lg py-2 px-3 break-all">
                    {secret}
                  </code>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Verification code
                </label>
                <div className="relative flex items-center">
                  <RiShieldCheckLine className="absolute left-3 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    autoFocus
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {error && <p className="text-sm text-red-500">{error}</p>}

              <button
                type="button"
                onClick={handleVerify}
                disabled={verifying || code.length !== 6}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium transition-colors ${
                  verifying || code.length !== 6
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-primary-700 text-white hover:bg-primary-800'
                }`}
              >
                <RiCheckLine className="w-4 h-4" />
                {verifying ? 'Verifying...' : 'Verify & enable'}
              </button>
            </>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default MfaEnrollModal;
